import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './InFlightMap.css';

// Import leaflet-rotate plugin functionality
// This allows aircraft icons to rotate based on heading
const setupLeafletRotate = () => {
    if (L.Marker.prototype._setPos && !L.Marker.prototype._setPos._rotated) {
        const originalSetPos = L.Marker.prototype._setPos;
        
        L.Marker.prototype._setPos = function(pos) {
            originalSetPos.call(this, pos);
            
            if (this.options.rotationAngle) {
                this._icon.style[L.DomUtil.TRANSFORM] += ' rotate(' + this.options.rotationAngle + 'deg)';
            }
        };
        
        L.Marker.prototype._setPos._rotated = true;
    }
};

const InFlightMap = forwardRef(({ flightData, onMarkerClick }, ref) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const featureGroupRef = useRef(null);


    useEffect(() => {
        // Initialize leaflet rotate functionality
        setupLeafletRotate();

        // Initialize map
        if (!mapInstanceRef.current) {
            const centerLat = 38.62; // Central USA
            const centerLon = -95.58;
            
            const map = L.map(mapRef.current).setView([centerLat, centerLon], 4);
            
            // Add OpenStreetMap tiles
            L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; OpenStreetMap'
            }).addTo(map);
            
            // Create feature group for markers
            const fg = L.featureGroup().addTo(map);
            
            mapInstanceRef.current = map;
            featureGroupRef.current = fg;
        }

        return () => {
            // Cleanup on unmount
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // Update markers when flight data changes
        if (!mapInstanceRef.current || !featureGroupRef.current || !flightData) return;

        const fg = featureGroupRef.current;
        const map = mapInstanceRef.current;

        // Clear existing markers
        fg.clearLayers();

        // Create aircraft icon
        const icon = L.icon({
            iconUrl: 'https://fdxva.org/plane_icon.png',
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            popupAnchor: [0, -28]
        });

        // Add markers for each flight
        flightData.forEach((flight, index) => {
            const { lat, lon, hdg, user, alt, spd, orig, dest } = flight;

            // Create popup content
            const popupString = `
                <div class="flight-popup">
                    <b>${user}</b><br />
                    Altitude: ${alt}<br />
                    Speed: ${spd}<br />
                    Heading: ${hdg}<br />
                    ${orig} - ${dest}
                </div>
            `;

            // Create marker with rotation
            const marker = L.marker([lat, lon], {
                icon: icon,
                rotationAngle: hdg,
                rotationOrigin: 'center center',
                xxid: index
            }).bindPopup(popupString);

            // Add click handler
            marker.on('click', () => {
                if (onMarkerClick) {
                    onMarkerClick(index, flight);
                }
                map.setView([lat, lon], 5);
            });

            marker.addTo(fg);
        });

    }, [flightData, onMarkerClick]);

    // Function to center map on specific flight (exposed via ref)
    const centerOnFlight = (flightIndex) => {
        if (!flightData || !flightData[flightIndex] || !mapInstanceRef.current) return;

        const flight = flightData[flightIndex];
        const map = mapInstanceRef.current;
        
        map.setView([flight.lat, flight.lon], 5);

        // Open popup for this marker
        featureGroupRef.current.eachLayer((layer) => {
            if (layer instanceof L.Marker && layer.options.xxid === flightIndex) {
                layer.openPopup();
            }
        });
    };

    // Expose centerOnFlight function via ref
    useImperativeHandle(ref, () => ({
        centerOnFlight
    }), [flightData]);

    return (
        <div className="inflight-map-container">
            <div ref={mapRef} className="inflight-map" style={{ width: '100%', height: '32rem' }}></div>
        </div>
    );
});

export default InFlightMap;
